// Normalized illustrative shapes: the UI communicates direction without
// presenting an unsourced population average as measured product data.
export const ONBOARDING_IMPACT_PERIOD_LABELS = [
  'DAY 1',
  '5',
  '10',
  '15',
  '20',
  '25',
  '30',
] as const;

// Both projections begin from the same illustrative baseline so they can be
// overlaid in one chart. The FocusCorp path still has ordinary good/bad-day
// movement while its broader direction remains clearly downward.
export const TYPICAL_SCREEN_TIME_TREND = [0.86, 0.97, 0.79, 0.94, 0.81, 0.98, 0.88] as const;
export const FOCUSCORP_SCREEN_TIME_TREND = [0.86, 0.64, 0.7, 0.45, 0.51, 0.29, 0.18] as const;

export const ONBOARDING_IMPACT_COMPARISON_SERIES = [
  {
    id: 'typical',
    label: 'TYPICAL',
    values: TYPICAL_SCREEN_TIME_TREND,
  },
  {
    id: 'focuscorp',
    label: 'WITH FOCUSCORP',
    values: FOCUSCORP_SCREEN_TIME_TREND,
  },
] as const;

export type OnboardingImpactChartPoint = {
  x: number;
  y: number;
};

export type OnboardingImpactChartGeometry = {
  areaPath: string;
  baselineY: number;
  linePath: string;
  points: OnboardingImpactChartPoint[];
};

export type OnboardingImpactProjection = {
  dailyFocusMinutes: number;
  dailyReclaimedLabel: string;
  monthlyBuildHours: number;
  sessionDurationMinutes: number;
  sessionsPerDay: number;
};

export type OnboardingImpactProjectionInput = {
  dailySessions?: unknown;
  focusDuration?: unknown;
};

const DEFAULT_DAILY_SESSIONS = 1;
const DEFAULT_FOCUS_DURATION_MINUTES = 25;
const MAX_DAILY_SESSIONS = 4;
const MAX_FOCUS_DURATION_MINUTES = 60;
const MIN_FOCUS_DURATION_MINUTES = 15;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function parseFirstFiniteNumber(value: unknown) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const match = value.trim().replace(',', '.').match(/[+-]?\d+(?:\.\d+)?/u);
  if (!match) {
    return undefined;
  }

  const parsedValue = Number(match[0]);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
}

function parseDailySessions(value: unknown) {
  const parsedValue = parseFirstFiniteNumber(value);
  if (parsedValue === undefined || parsedValue <= 0) {
    return DEFAULT_DAILY_SESSIONS;
  }

  // The onboarding's highest choice is "4+". Four is the conservative
  // projection for that open-ended answer rather than inventing extra sessions.
  return clamp(Math.floor(parsedValue), 1, MAX_DAILY_SESSIONS);
}

function parseFocusDurationMinutes(value: unknown) {
  const parsedValue = parseFirstFiniteNumber(value);
  if (parsedValue === undefined || parsedValue <= 0) {
    return DEFAULT_FOCUS_DURATION_MINUTES;
  }

  // Likewise, "60 min+" is projected as exactly one hour. This keeps the
  // forecast grounded in the minimum commitment the user explicitly selected.
  return clamp(
    Math.round(parsedValue),
    MIN_FOCUS_DURATION_MINUTES,
    MAX_FOCUS_DURATION_MINUTES,
  );
}

export function getProjectedMonthlyCompanyFocusHours(
  dailySessions: unknown,
  focusDuration: unknown,
) {
  const sessionsPerDay = parseDailySessions(dailySessions);
  const sessionDurationMinutes = parseFocusDurationMinutes(focusDuration);
  const rawMonthlyHours = (sessionsPerDay * sessionDurationMinutes * 30) / 60;

  // Half-hours remain honest for 15/25-minute plans while keeping the headline
  // compact (for example, 25 min x 1 per day = 12.5 hours per 30 days).
  return Math.round(rawMonthlyHours * 2) / 2;
}

function formatDailyFocusLabel(minutes: number) {
  const wholeHours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (wholeHours === 0) {
    return `${remainingMinutes} MIN OF FOCUS / DAY`;
  }

  if (remainingMinutes === 0) {
    return `${wholeHours} ${wholeHours === 1 ? 'HOUR' : 'HOURS'} OF FOCUS / DAY`;
  }

  return `${wholeHours}H ${remainingMinutes}MIN OF FOCUS / DAY`;
}

export function buildSmoothLinePath(points: readonly OnboardingImpactChartPoint[]) {
  const firstPoint = points[0];

  if (!firstPoint) {
    return '';
  }

  if (points.length === 1) {
    return `M ${firstPoint.x} ${firstPoint.y}`;
  }

  let path = `M ${firstPoint.x} ${firstPoint.y}`;

  for (let index = 0; index < points.length - 1; index += 1) {
    const currentPoint = points[index] ?? firstPoint;
    const nextPoint = points[index + 1] ?? currentPoint;
    const previousPoint = points[index - 1] ?? currentPoint;
    const followingPoint = points[index + 2] ?? nextPoint;
    const controlPoint1X = currentPoint.x + (nextPoint.x - previousPoint.x) / 6;
    const controlPoint1Y = currentPoint.y + (nextPoint.y - previousPoint.y) / 6;
    const controlPoint2X = nextPoint.x - (followingPoint.x - currentPoint.x) / 6;
    const controlPoint2Y = nextPoint.y - (followingPoint.y - currentPoint.y) / 6;

    path += ` C ${controlPoint1X} ${controlPoint1Y}, ${controlPoint2X} ${controlPoint2Y}, ${nextPoint.x} ${nextPoint.y}`;
  }

  return path;
}

export function buildOnboardingImpactChartGeometry(
  values: readonly number[],
  width = 320,
  height = 78,
): OnboardingImpactChartGeometry {
  const paddingHorizontal = 8;
  const paddingTop = 8;
  const paddingBottom = 7;
  const baselineY = height - paddingBottom;
  const plotWidth = width - paddingHorizontal * 2;
  const plotHeight = baselineY - paddingTop;
  const stepX = values.length > 1 ? plotWidth / (values.length - 1) : 0;
  const points = values.map((value, index) => ({
    x: paddingHorizontal + (values.length === 1 ? plotWidth / 2 : stepX * index),
    y: paddingTop + (1 - clamp(value, 0, 1)) * plotHeight,
  }));
  const linePath = buildSmoothLinePath(points);
  const firstPoint = points[0];
  const lastPoint = points.at(-1);
  const areaPath =
    linePath && firstPoint && lastPoint
      ? `${linePath} L ${lastPoint.x} ${baselineY} L ${firstPoint.x} ${baselineY} Z`
      : '';

  return {
    areaPath,
    baselineY,
    linePath,
    points,
  };
}

export function getOnboardingImpactProjection(
  input: OnboardingImpactProjectionInput | number,
  legacyDailySessions?: unknown,
  legacyFocusDuration?: unknown,
): OnboardingImpactProjection {
  // Accept the previous numeric first argument until every caller has moved to
  // the named input. Phone usage is intentionally no longer used to invent a
  // fixed 30/60-minute result; the user's own focus plan drives the forecast.
  const dailySessions =
    typeof input === 'object' && input !== null
      ? input.dailySessions
      : legacyDailySessions;
  const focusDuration =
    typeof input === 'object' && input !== null
      ? input.focusDuration
      : legacyFocusDuration;
  const sessionsPerDay = parseDailySessions(dailySessions);
  const sessionDurationMinutes = parseFocusDurationMinutes(focusDuration);
  const dailyFocusMinutes = sessionsPerDay * sessionDurationMinutes;

  return {
    dailyFocusMinutes,
    dailyReclaimedLabel: formatDailyFocusLabel(dailyFocusMinutes),
    monthlyBuildHours: getProjectedMonthlyCompanyFocusHours(
      sessionsPerDay,
      sessionDurationMinutes,
    ),
    sessionDurationMinutes,
    sessionsPerDay,
  };
}
